import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Activity, CheckCircle2, ClipboardList, FolderOpen, Plus, RefreshCcw, Ticket, Trash2, Building2, UserSquare } from 'lucide-react';
import {
  addPmTicketComment,
  createPmTask,
  createPmTicket,
  deletePmTask,
  getPmActivityLogs,
  getPmClients,
  getPmDashboard,
  getPmKanban,
  getPmMembers,
  getPmProjects,
  getPmStatuses,
  getPmTicketComments,
  getPmTickets,
  getPmTicketTemplates,
  movePmTask,
} from './api/projectManagementApi';
import './ProjectManagement.css';

const emptyTask = {
  title: '',
  projectId: '',
  assignedTo: '',
  points: 1,
};

const emptyTicket = {
  title: '',
  projectId: '',
  assignedTo: '',
};

const ALL_TABS = ['dashboard', 'projects', 'clients', 'members', 'kanban', 'tickets', 'templates', 'activity'];
const HISTORY_TABS = ['clients', 'members', 'kanban', 'tickets', 'templates', 'activity'];

const ProjectManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isHistoryModule = location.pathname.startsWith('/pm/history');
  const validTabs = isHistoryModule ? HISTORY_TABS : ALL_TABS;
  const urlTab = new URLSearchParams(location.search).get('tab');
  const initialTab = validTabs.includes(urlTab) ? urlTab : (isHistoryModule ? 'clients' : 'dashboard');
  const [activeTab, setActiveTabState] = useState(initialTab);

  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    navigate(`${isHistoryModule ? '/pm/history' : '/project-management'}?tab=${tab}`, { replace: true });
  };

  useEffect(() => {
    const t = new URLSearchParams(location.search).get('tab');
    const nextTab = validTabs.includes(t) ? t : (isHistoryModule ? 'clients' : 'dashboard');
    if (nextTab !== activeTab) setActiveTabState(nextTab);
  }, [location.search, activeTab, isHistoryModule, validTabs]);
  const [dashboard, setDashboard] = useState({});
  const [kanban, setKanban] = useState({ columns: [] });
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [members, setMembers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [comments, setComments] = useState([]);
  const [taskForm, setTaskForm] = useState(emptyTask);
  const [ticketForm, setTicketForm] = useState(emptyTicket);
  const [commentText, setCommentText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const firstStatusId = useMemo(() => statuses?.[0]?.id, [statuses]);

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [dashboardData, kanbanData, projectData, clientData, memberData, statusData, ticketData, templateData, logData] = await Promise.all([
        getPmDashboard(),
        getPmKanban(),
        getPmProjects(),
        getPmClients(),
        getPmMembers(),
        getPmStatuses(),
        getPmTickets(),
        getPmTicketTemplates(),
        getPmActivityLogs(),
      ]);
      setDashboard(dashboardData || {});
      setKanban(kanbanData || { columns: [] });
      setProjects(projectData || []);
      setClients(clientData || []);
      setMembers(memberData || []);
      setStatuses(statusData || []);
      setTickets(ticketData || []);
      setTemplates(templateData || []);
      setLogs(logData || []);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to load Project Management data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    if (!selectedTicketId) {
      setComments([]);
      return;
    }
    getPmTicketComments(selectedTicketId)
      .then(setComments)
      .catch(() => setComments([]));
  }, [selectedTicketId]);

  // OneAlpha palette — teal primary (#1f8f98) + supporting accents
  const metrics = [
    { label: 'Total Tasks', value: dashboard.total_tasks || 0, tab: 'kanban', color: '#1f8f98', icon: ClipboardList },
    { label: 'To Do', value: dashboard.todo_tasks || 0, tab: 'kanban', color: '#F9A52D', icon: ClipboardList },
    { label: 'In Progress', value: dashboard.in_progress_tasks || 0, tab: 'kanban', color: '#1672B9', icon: Activity },
    { label: 'Done', value: dashboard.done_tasks || 0, tab: 'kanban', color: '#00B393', icon: CheckCircle2 },
    { label: 'Projects', value: dashboard.total_projects || 0, tab: 'projects', color: '#0a7c8a', icon: FolderOpen },
    { label: 'Clients', value: dashboard.total_clients || 0, tab: 'clients', color: '#2BB8C5', icon: Building2 },
    { label: 'Project Members', value: dashboard.project_members || 0, tab: 'members', color: '#0d6e76', icon: UserSquare },
    { label: 'Tickets', value: dashboard.total_tickets || 0, tab: 'tickets', color: '#fd7e14', icon: Ticket },
    { label: 'Open Tickets', value: dashboard.open_tickets || 0, tab: 'tickets', color: '#dc3545', icon: Ticket },
    { label: 'Activity Logs', value: dashboard.activity_logs || 0, tab: 'activity', color: '#6c757d', icon: Activity },
  ];

  const projectsByStatus = Array.isArray(dashboard.projects_by_status) ? dashboard.projects_by_status : [];
  const topClients = Array.isArray(dashboard.top_clients) ? dashboard.top_clients : [];
  const recentProjects = Array.isArray(dashboard.recent_projects) ? dashboard.recent_projects : [];
  const maxStatusCount = projectsByStatus.reduce((max, s) => Math.max(max, Number(s.count) || 0), 0);
  const maxClientCount = topClients.reduce((max, c) => Math.max(max, Number(c.project_count) || 0), 0);
  // OneAlpha teal-based status palette (no purple)
  const STATUS_COLORS = { open: '#1672B9', completed: '#00B393', canceled: '#dc3545', hold: '#F9A52D', unknown: '#6c757d' };

  const submitTask = async (event) => {
    event.preventDefault();
    if (!taskForm.title.trim()) return;
    await createPmTask({
      ...taskForm,
      statusId: firstStatusId,
      projectId: taskForm.projectId || 0,
      assignedTo: taskForm.assignedTo || 0,
      points: taskForm.points || 1,
      context: 'project',
    });
    setTaskForm(emptyTask);
    loadAll();
  };

  const submitTicket = async (event) => {
    event.preventDefault();
    if (!ticketForm.title.trim()) return;
    await createPmTicket({
      ...ticketForm,
      projectId: ticketForm.projectId || 0,
      assignedTo: ticketForm.assignedTo || 0,
      status: 'new',
    });
    setTicketForm(emptyTicket);
    loadAll();
  };

  const submitComment = async (event) => {
    event.preventDefault();
    if (!selectedTicketId || !commentText.trim()) return;
    await addPmTicketComment(selectedTicketId, { description: commentText });
    setCommentText('');
    setComments(await getPmTicketComments(selectedTicketId));
    loadAll();
  };

  const applyTemplate = (templateId) => {
    const template = templates.find((item) => String(item.id) === String(templateId));
    if (template) {
      setCommentText(template.description || '');
    }
  };

  return (
    <div className="pm-page">
      <div className="pm-header">
        <div>
          <div className="pm-title">
            <ClipboardList size={28} color="#1f8f98" />
            <h1>{isHistoryModule ? 'History' : 'Project Management'}</h1>
          </div>
          <p className="pm-subtitle">
            {isHistoryModule
              ? 'Clients, members, Kanban, tickets, templates, and activity logs.'
              : 'OneAlpha task, Kanban, ticket, and activity workspace.'}
          </p>
        </div>
        <button className="btn btn-outline-primary d-inline-flex align-items-center gap-2" onClick={loadAll} disabled={loading}>
          <RefreshCcw size={16} /> Refresh
        </button>
      </div>

      <div className="pm-tabs" role="tablist">
        {[
          ...(!isHistoryModule ? [
            ['dashboard', 'Dashboard'],
            ['projects', 'Projects'],
          ] : []),
          ['clients', 'Clients'],
          ['members', 'Project Members'],
          ['kanban', 'Kanban'],
          ['tickets', 'Tickets'],
          ['templates', 'Ticket Templates'],
          ['activity', 'Activity'],
        ].map(([key, label]) => (
          <button key={key} type="button" className={`pm-tab ${activeTab === key ? 'active' : ''}`} onClick={() => setActiveTab(key)}>
            {label}
          </button>
        ))}
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {activeTab === 'dashboard' && (
        <>
          <div className="pm-grid">
            {metrics.map((m) => {
              const Ico = m.icon;
              return (
                <button
                  type="button"
                  className="pm-card pm-metric-card"
                  key={m.label}
                  onClick={() => setActiveTab(m.tab)}
                  style={{ borderLeft: `4px solid ${m.color}` }}
                  aria-label={`Open ${m.label}`}
                >
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <span className="pm-metric-label">{m.label}</span>
                    <Ico size={16} color={m.color} />
                  </div>
                  <div className="pm-metric-value" style={{ color: m.color }}>{m.value}</div>
                </button>
              );
            })}
          </div>

          <div className="row g-3">
            <div className="col-12 col-lg-6">
              <div className="pm-card h-100">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <FolderOpen size={18} color="#1f8f98" />
                  <strong>Projects by Status</strong>
                </div>
                {projectsByStatus.length ? (
                  <div className="pm-bars">
                    {projectsByStatus.map((s) => {
                      const count = Number(s.count) || 0;
                      const pct = maxStatusCount ? Math.max(4, (count / maxStatusCount) * 100) : 0;
                      const color = STATUS_COLORS[s.status_key] || '#1f8f98';
                      return (
                        <div className="pm-bar-row" key={s.status_key}>
                          <div className="pm-bar-label">{s.status_title || s.status_key}</div>
                          <div className="pm-bar-track">
                            <div className="pm-bar-fill" style={{ width: `${pct}%`, background: color }} />
                          </div>
                          <div className="pm-bar-count">{count}</div>
                        </div>
                      );
                    })}
                  </div>
                ) : <div className="pm-empty">No project status data</div>}
              </div>
            </div>

            <div className="col-12 col-lg-6">
              <div className="pm-card h-100">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <FolderOpen size={18} color="#1f8f98" />
                  <strong>Top Clients by Projects</strong>
                </div>
                {topClients.length ? (
                  <div className="pm-bars">
                    {topClients.map((c) => {
                      const count = Number(c.project_count) || 0;
                      const pct = maxClientCount ? Math.max(4, (count / maxClientCount) * 100) : 0;
                      return (
                        <div className="pm-bar-row" key={c.client_id}>
                          <div className="pm-bar-label" title={c.company_name}>{c.company_name || '-'}</div>
                          <div className="pm-bar-track">
                            <div className="pm-bar-fill" style={{ width: `${pct}%`, background: '#1f8f98' }} />
                          </div>
                          <div className="pm-bar-count">{count}</div>
                        </div>
                      );
                    })}
                  </div>
                ) : <div className="pm-empty">No client data</div>}
              </div>
            </div>
          </div>

          <div className="pm-card mt-3">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center gap-2">
                <FolderOpen size={18} color="#1f8f98" />
                <strong>Recent Projects</strong>
              </div>
              <button type="button" className="btn btn-sm btn-link p-0" onClick={() => setActiveTab('projects')}>View all →</button>
            </div>
            {recentProjects.length ? (
              <div className="table-responsive">
                <table className="pm-table">
                  <thead>
                    <tr><th>Project</th><th>Client</th><th>Status</th><th>Deadline</th><th>Created</th></tr>
                  </thead>
                  <tbody>
                    {recentProjects.map((p) => (
                      <tr key={p.id}>
                        <td className="pm-table-title">{p.title}</td>
                        <td>{p.client_name || '-'}</td>
                        <td><span className="badge bg-light text-dark">{p.status || '-'}</span></td>
                        <td>{p.deadline ? new Date(p.deadline).toLocaleDateString() : '-'}</td>
                        <td>{p.created_date ? new Date(p.created_date).toLocaleDateString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <div className="pm-empty">No recent projects</div>}
          </div>

          <div className="pm-card mt-3">
            <div className="d-flex align-items-center gap-2 mb-3">
              <Activity size={18} color="#1f8f98" />
              <strong>Latest Activity</strong>
            </div>
            <ActivityTable logs={logs.slice(0, 8)} />
          </div>
        </>
      )}

      {activeTab === 'projects' && (
        <div className="pm-card">
          <div className="d-flex align-items-center gap-2 mb-3">
            <FolderOpen size={18} color="#1f8f98" />
            <strong>Legacy PM Projects</strong>
          </div>
          <div className="table-responsive">
            <table className="pm-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Client</th>
                  <th>Status</th>
                  <th>Deadline</th>
                  <th>Tasks</th>
                  <th>Members</th>
                </tr>
              </thead>
              <tbody>
                {projects.length ? projects.map((project) => (
                  <tr key={project.id}>
                    <td>
                      <div className="pm-table-title">{project.title}</div>
                      <div className="pm-muted">#{project.legacy_id} {project.project_type || ''}</div>
                    </td>
                    <td>{project.client_name || project.client_id || '-'}</td>
                    <td>{project.status_title || project.status || '-'}</td>
                    <td>{project.deadline ? new Date(project.deadline).toLocaleDateString() : '-'}</td>
                    <td>{project.task_count || 0}</td>
                    <td>{project.member_count || 0}</td>
                  </tr>
                )) : (
                  <tr><td className="pm-empty" colSpan="6">No projects found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'clients' && (
        <div className="pm-card">
          <div className="d-flex align-items-center gap-2 mb-3">
            <Building2 size={18} color="#1f8f98" />
            <strong>PM Clients</strong>
            <span className="badge bg-light text-dark ms-auto">{clients.length} total</span>
          </div>
          {clients.length ? (
            <div className="table-responsive">
              <table className="pm-table">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Type</th>
                    <th>Location</th>
                    <th>Phone</th>
                    <th>Currency</th>
                    <th className="text-end">Projects</th>
                    <th className="text-end">Tasks</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <div className="pm-table-title">{c.company_name || '-'}</div>
                        <div className="pm-muted">#{c.legacy_id} {c.website ? `· ${c.website}` : ''}</div>
                      </td>
                      <td><span className="badge bg-light text-dark">{c.type || '-'}</span></td>
                      <td>{[c.city, c.state, c.country].filter(Boolean).join(', ') || '-'}</td>
                      <td>{c.phone || '-'}</td>
                      <td>{c.currency_symbol || '-'}</td>
                      <td className="text-end">{c.project_count || 0}</td>
                      <td className="text-end">{c.task_count || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="pm-empty">No clients found</div>}
        </div>
      )}

      {activeTab === 'members' && (
        <div className="pm-card">
          <div className="d-flex align-items-center gap-2 mb-3">
            <UserSquare size={18} color="#1f8f98" />
            <strong>Project Members</strong>
            <span className="badge bg-light text-dark ms-auto">{members.length} total</span>
          </div>
          {members.length ? (
            <div className="table-responsive">
              <table className="pm-table">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Client</th>
                    <th>User ID</th>
                    <th>Role</th>
                    <th className="text-end">Tasks</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id}>
                      <td>
                        <div className="pm-table-title">{m.project_title || `Project #${m.project_id}`}</div>
                        <div className="pm-muted">#{m.legacy_id}</div>
                      </td>
                      <td>{m.client_name || '-'}</td>
                      <td>{m.user_id || '-'}</td>
                      <td>
                        {m.is_leader
                          ? <span className="badge" style={{ background: '#1f8f98', color: '#fff' }}>Leader</span>
                          : <span className="badge bg-light text-dark">Member</span>}
                      </td>
                      <td className="text-end">{m.task_count || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="pm-empty">No project members found</div>}
        </div>
      )}

      {activeTab === 'kanban' && (
        <>
          <div className="pm-card mb-3">
            <form className="pm-form" onSubmit={submitTask}>
              <input className="form-control" placeholder="Task title" value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} />
              <input className="form-control" placeholder="Project ID" value={taskForm.projectId} onChange={(e) => setTaskForm({ ...taskForm, projectId: e.target.value })} />
              <input className="form-control" placeholder="Assigned user ID" value={taskForm.assignedTo} onChange={(e) => setTaskForm({ ...taskForm, assignedTo: e.target.value })} />
              <input className="form-control" type="number" min="1" placeholder="Points" value={taskForm.points} onChange={(e) => setTaskForm({ ...taskForm, points: e.target.value })} />
              <button className="btn btn-primary d-inline-flex align-items-center gap-2" type="submit">
                <Plus size={16} /> Task
              </button>
            </form>
          </div>
          <div className="pm-kanban">
            {(kanban.columns || []).map((column) => (
              <div className="pm-column" key={column.id}>
                <div className="pm-column-header">
                  <span><span className="pm-dot" style={{ background: column.color }} />{column.title}</span>
                  <span>{column.tasks?.length || 0}</span>
                </div>
                {column.tasks?.length ? column.tasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    statuses={statuses}
                    onMove={(statusId) => movePmTask(task.id, statusId).then(loadAll)}
                    onDelete={() => deletePmTask(task.id).then(loadAll)}
                  />
                )) : <div className="pm-empty">No tasks</div>}
              </div>
            ))}
          </div>
        </>
      )}

      {activeTab === 'tickets' && (
        <>
          <div className="pm-card mb-3">
            <form className="pm-form ticket-form" onSubmit={submitTicket}>
              <input className="form-control" placeholder="Ticket title" value={ticketForm.title} onChange={(e) => setTicketForm({ ...ticketForm, title: e.target.value })} />
              <input className="form-control" placeholder="Project ID" value={ticketForm.projectId} onChange={(e) => setTicketForm({ ...ticketForm, projectId: e.target.value })} />
              <input className="form-control" placeholder="Assigned user ID" value={ticketForm.assignedTo} onChange={(e) => setTicketForm({ ...ticketForm, assignedTo: e.target.value })} />
              <button className="btn btn-primary d-inline-flex align-items-center gap-2" type="submit">
                <Plus size={16} /> Ticket
              </button>
            </form>
          </div>
          <div className="pm-card">
            <div className="table-responsive">
              <table className="pm-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Status</th>
                    <th>Project</th>
                    <th>Assigned</th>
                    <th>Comments</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.length ? tickets.map((ticket) => (
                    <tr key={ticket.id}>
                      <td><Ticket size={15} className="me-2" />{ticket.title}</td>
                      <td>{ticket.status}</td>
                      <td>{ticket.project_id || '-'}</td>
                      <td>{ticket.assigned_to || '-'}</td>
                      <td><button className="btn btn-sm btn-outline-primary" onClick={() => setSelectedTicketId(ticket.id)}>Open</button></td>
                    </tr>
                  )) : (
                    <tr><td className="pm-empty" colSpan="5">No tickets found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {selectedTicketId && (
            <div className="pm-card mt-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <strong>Ticket #{selectedTicketId}</strong>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setSelectedTicketId(null)}>Close</button>
              </div>
              <form className="mb-3" onSubmit={submitComment}>
                <div className="row g-2">
                  <div className="col-md-3">
                    <select className="form-select" onChange={(e) => applyTemplate(e.target.value)} defaultValue="">
                      <option value="">Template</option>
                      {templates.map((template) => <option value={template.id} key={template.id}>{template.title}</option>)}
                    </select>
                  </div>
                  <div className="col-md-7">
                    <input className="form-control" placeholder="Comment" value={commentText} onChange={(e) => setCommentText(e.target.value)} />
                  </div>
                  <div className="col-md-2">
                    <button className="btn btn-primary w-100" type="submit">Send</button>
                  </div>
                </div>
              </form>
              {comments.length ? comments.map((comment) => (
                <div className="border-top py-2" key={comment.id}>{comment.description}</div>
              )) : <div className="pm-empty">No comments</div>}
            </div>
          )}
        </>
      )}

      {activeTab === 'templates' && (
        <div className="pm-card">
          <h4 className="mb-3">Ticket Templates</h4>
          {templates.length ? (
            <div className="table-responsive">
              <table className="pm-table">
                <thead>
                  <tr><th>Title</th><th>Description</th></tr>
                </thead>
                <tbody>
                  {templates.map((tpl) => (
                    <tr key={tpl.id}>
                      <td>{tpl.title || '-'}</td>
                      <td>{tpl.description || tpl.body || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="pm-empty">No templates</div>}
        </div>
      )}

      {activeTab === 'activity' && (
        <div className="pm-card">
          <ActivityTable logs={logs} />
        </div>
      )}
    </div>
  );
};

const TaskCard = ({ task, statuses, onMove, onDelete }) => (
  <div className="pm-task">
    <div className="pm-task-title">{task.title}</div>
    <div className="pm-task-meta">
      <span>Project: {task.project_id || '-'}</span>
      <span>Assigned: {task.assigned_to || '-'}</span>
      <span>Points: {task.points || 1}</span>
    </div>
    <div className="pm-task-actions">
      <select className="form-select form-select-sm" value={task.status_id || ''} onChange={(e) => onMove(e.target.value)}>
        {statuses.map((status) => <option value={status.id} key={status.id}>{status.title}</option>)}
      </select>
      <button className="btn btn-sm btn-outline-danger" type="button" onClick={onDelete} aria-label="Delete task">
        <Trash2 size={15} />
      </button>
    </div>
  </div>
);

const ActivityTable = ({ logs }) => (
  <div className="table-responsive">
    <table className="pm-table">
      <thead>
        <tr>
          <th>Action</th>
          <th>Type</th>
          <th>Title</th>
          <th>Time</th>
        </tr>
      </thead>
      <tbody>
        {logs?.length ? logs.map((log) => (
          <tr key={log.id}>
            <td><CheckCircle2 size={15} className="me-2 text-success" />{log.action}</td>
            <td>{log.log_type}</td>
            <td>{log.log_type_title}</td>
            <td>{log.created_at ? new Date(log.created_at).toLocaleString() : '-'}</td>
          </tr>
        )) : (
          <tr><td className="pm-empty" colSpan="4">No activity found</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

export default ProjectManagement;
